import { HttpClient, HttpEventType } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  NotificationMessageIcon,
  RetryServices,
} from '@app/framework/notification-sidebar/notification-sidebar.component';
import { environment } from '@env/environment';
import { HttpUtilService } from '@services/http-util.service';
import { NotificationsService } from '@services/notifications.service';
import random from 'lodash/random';
import cloneDeep from 'lodash/cloneDeep';
import { TranslationService } from '@services/translation.service';
import { WebSocketService } from '@services/web-socket.service';

@Injectable({
  providedIn: 'root',
})
export class ZipService {
  public foldersToZip = {};
  constructor(
    private notifications: NotificationsService,
    public webSocketService: WebSocketService,
    public http: HttpUtilService,
    private httpClient: HttpClient,
    private translationService: TranslationService
  ) {}

  async zipFiles(
    files = [],
    folders = [],
    prefix?,
    avoidPopups?,
    skipZip = true
  ) {
    const id = String(random(1, 999999));
    const isSingleFile = files?.length === 1 && !(folders?.length >= 1);
    if (!avoidPopups) {
      this.notifications.addUserNotification(
        {
          icon: NotificationMessageIcon.fileZip,
          text: `${this.translationService.getTranslatedContent('preparing', 'Preparing')} ${
            isSingleFile
              ? files?.[0]?.title
              : this.translationService.getTranslatedContent('zip', 'Zip')
          } ${this.translationService.getTranslatedContent('to_be_downloaded', 'to be downloaded')}`,
          progress: '0',
          preventRemoval: true,
          shouldForceOpen: true,
          status: `0%`,
        },
        id
      );
    }

    this.foldersToZip[id] = { id, files, folders, prefix, skipZip };
    this.startZipFileProcess(id);

    const subscription = this.webSocketService
      .subscribeToMessages('zip-download')
      .subscribe(message => {
        if (message.requestId === id) {
          if (message.progress && !avoidPopups) {
            this.notifications.addUserNotification(
              {
                text: `${this.translationService.getTranslatedContent('preparing_zip_for_download', 'Preparing Zip for download')}`,
                progress: message.progress,
                preventRemoval: true,
                status: `${message.progress}%`,
                icon: NotificationMessageIcon.fileZip,
              },
              id
            );
          }
          if (message?.signedUrls) {
            if (message?.skipZip) {
              subscription.unsubscribe();
              return this.downloadFile(message?.signedUrls[0], id);
            }
            if (message?.fileName) {
              subscription.unsubscribe();
              setTimeout(() => {
                this.downloadZip(message?.signedUrls[0], id, message?.fileName);
              }, 750);
            } else {
              subscription.unsubscribe();
              message?.signedUrls.forEach(signedUrl => {
                setTimeout(() => {
                  this.downloadZip(signedUrl, id);
                }, 750);
              });
            }
          }
          if (message?.error || !message) {
            subscription.unsubscribe();
            if (!avoidPopups) return;
            const text = this.translationService.getTranslatedContent(
              'no_files_to_zip',
              'No Files to Zip'
            );
            if (message.error === 'No Files to Zip') {
              this.notifications.popNotification('', text);
            }
            this.handleGenericZipError(id);
          }
        }
      });

    this.foldersToZip[id].subscription = subscription;
  }

  async zipFolder(folder, prefix?) {
    if (this.foldersToZip[folder.id]) return;
    this.notifications.addUserNotification(
      {
        icon: NotificationMessageIcon.fileZip,
        text: `${this.translationService.getTranslatedContent('preparing', 'Preparing')} <b>${folder?.data?.title || ''}</b> ${this.translationService.getTranslatedContent('for_download', 'for download')}`,
        progress: '0',
        preventRemoval: true,
        shouldForceOpen: true,
        status: `0%`,
      },
      folder.id
    );
    this.foldersToZip[folder.id] = { folder, prefix };

    const subscription = this.webSocketService
      .subscribeToMessages('zip-download')
      .subscribe(message => {
        if (message.requestId === folder.id) {
          if (message.progress) {
            this.notifications.addUserNotification(
              {
                text: `${this.translationService.getTranslatedContent('preparing', 'Preparing')} <b>${folder?.data?.title || ''}</b> ${this.translationService.getTranslatedContent('for_download', 'for download')}`,
                progress: message.progress,
                preventRemoval: true,
                status: `${message.progress}%`,
                icon: NotificationMessageIcon.fileZip,
              },
              folder.id
            );
          }

          if (message?.signedUrls) {
            subscription.unsubscribe();
            if (message?.fileName) {
              setTimeout(() => {
                this.downloadZipFile(message?.signedUrls[0], folder, 0, prefix);
              }, 750);
            } else {
              message?.signedUrls.forEach((signedUrl, i) => {
                setTimeout(() => {
                  this.downloadZipFile(signedUrl, folder, i, prefix);
                }, 750);
              });
            }
          }

          if (message?.error || !message) {
            subscription.unsubscribe();
            if (message.error === 'No Files to Zip') {
              const text = this.translationService.getTranslatedContent(
                'no_files_to_zip',
                'No Files to Zip'
              );
              this.notifications.popNotification('', text);
            }
            this.handleGenericError(folder);
          }
        }
      });

    this.foldersToZip[folder.id].subscription = subscription;
    this.startZipProcess(folder.id);
  }

  // avoid using the data table to gather folder content for certain cases, just list the files to be retrieved in a zipped manner
  async zipFolderS3Only(key: string, folderName: string) {
    const folder = { data: { title: folderName }, id: key };
    if (this.foldersToZip[key]) return;
    this.notifications.addUserNotification(
      {
        icon: NotificationMessageIcon.fileZip,
        text: `${this.translationService.getTranslatedContent('preparing', 'Preparing')} <b>${folderName}</b> ${this.translationService.getTranslatedContent('for_download', 'for download')}`,
        progress: '0',
        preventRemoval: true,
        shouldForceOpen: true,
        status: `0%`,
      },
      key
    );

    this.foldersToZip[key] = {
      folder,
    };

    const subscription = this.webSocketService
      .subscribeToMessages('zip-download')
      .subscribe(message => {
        if (message.requestId === key) {
          if (message.progress) {
            this.notifications.addUserNotification(
              {
                text: `${this.translationService.getTranslatedContent('preparing', 'Preparing')} <b>${folderName || ''}</b> ${this.translationService.getTranslatedContent('for_download', 'for download')}`,
                progress: message.progress,
                preventRemoval: true,
                status: `${message.progress}%`,
                icon: NotificationMessageIcon.fileZip,
              },
              key
            );
          }

          if (message?.signedUrls) {
            subscription.unsubscribe();
            if (message?.fileName) {
              setTimeout(() => {
                this.downloadZipFile(message?.signedUrls[0], folder, 0, key);
              }, 750);
            } else {
              message?.signedUrls.forEach((signedUrl, i) => {
                setTimeout(() => {
                  this.downloadZipFile(signedUrl, folder, i, key);
                }, 750);
              });
            }
          }

          if (message?.error || !message) {
            subscription.unsubscribe();
            if (message.error === 'No Files to Zip') {
              const text = this.translationService.getTranslatedContent(
                'no_files_to_zip',
                'No Files to Zip'
              );
              this.notifications.popNotification('', text);
            }
            this.handleGenericError(folder);
          }
        }
      });

    this.foldersToZip[key].subscription = subscription;
    this.startZipFolderS3DirectProcess(folder, key);
  }

  startZipFolderS3DirectProcess(folder, key) {
    const url = `/api/download/zip-init?s3KeyOnly=${key}&request=${key}`;
    this.http.request('POST', url, {}).subscribe(
      () => {},
      err => {
        this.handleGenericZipError(folder);
      }
    );
  }

  startZipFileProcess(id) {
    let { files, folders, prefix, skipZip } = this.foldersToZip[id];
    skipZip = skipZip && files?.length === 1 && !folders?.length;
    let url = `/api/download/zip-init?request=${id}`;
    if (skipZip) url += '&skipZip=true';
    this.http
      .request('POST', url, { files, folders, prefix }, true, true)
      .subscribe(
        () => {},
        err => {
          this.handleGenericZipError(id);
        }
      );
  }

  startZipProcess(id) {
    const { folder, prefix, skipZip } = this.foldersToZip[id];
    let url = `/api/download/zip-init?id=${id}&request=${id}&prefix=${prefix}`;
    if (skipZip) url += '&skipZip=true';
    this.http.request('GET', url, null, true, true).subscribe(
      () => {},
      err => {
        this.handleGenericError(folder);
      }
    );
  }

  handleGenericZipError(id) {
    // hopefully avoids circular dependency
    const data = cloneDeep(this.foldersToZip[id]);
    data?.files?.forEach(file => delete file?.route);
    data?.folders?.forEach(folder => delete folder?.route);

    this.notifications.addUserNotification(
      {
        icon: NotificationMessageIcon.fail,
        retry: {
          service: RetryServices.zipWithFilesAndFolders,
          data,
        },
        text: this.translationService.getTranslatedContent(
          'preparing_file_for_download_has_failed',
          `Preparing File for download has failed`
        ),
        status: this.translationService.getTranslatedContent(
          'download_failed',
          'Download Failed'
        ),
      },
      id
    );
    if (this.foldersToZip[id]?.socket) {
      this.foldersToZip[id]?.socket?.close();
    }
    if (this.foldersToZip[id]?.subscription) {
      this.foldersToZip[id]?.subscription?.unsubscribe();
    }
    delete this.foldersToZip[id];
  }

  handleGenericError(folder) {
    // hopefully avoids circular dependency
    const data = cloneDeep(folder);
    delete data.route;
    this.notifications.addUserNotification(
      {
        icon: NotificationMessageIcon.fail,
        retry: {
          service: RetryServices.zip,
          data,
        },
        text:
          this.translationService.getTranslatedContent(
            'preparing_file_for_download_has_failed',
            `Preparing File for download has failed`
          ) + ` <b>${folder?.data?.title || ''}</b>`,
        status: this.translationService.getTranslatedContent(
          'download_failed',
          'Download Failed'
        ),
      },
      folder.id
    );
    if (this.foldersToZip[folder.id]?.socket) {
      this.foldersToZip[folder.id]?.socket?.close();
    }
    if (this.foldersToZip[folder.id]?.subscription) {
      this.foldersToZip[folder.id]?.subscription?.unsubscribe();
    }
    delete this.foldersToZip[folder.id];
  }

  downloadFile(signedUrl: string, id, filename?: string) {
    const fileName = filename || this.foldersToZip[id]?.files[0]?.title;

    this.httpClient
      .get(signedUrl, {
        responseType: 'blob',
        reportProgress: true,
        observe: 'events',
      })
      .subscribe(
        res => {
          if (res.type === HttpEventType.DownloadProgress) {
            const percentDone = Math.round((100 * res.loaded) / res.total);
            this.notifications.addUserNotification(
              {
                icon: NotificationMessageIcon.fileZip,
                text: `${this.translationService.getTranslatedContent('downloading', 'Downloading')} ${fileName}`,
                progress: percentDone,
                status: `${this.translationService.getTranslatedContent('downloading', 'Downloading')}`,
              },
              id
            );
          }
          if (res.type === HttpEventType.Response) {
            res.body;
            const a = document.createElement('a');
            document.body.appendChild(a);
            a.style.display = 'none';
            const url = window.URL.createObjectURL(res.body);
            a.href = url;
            a.download = fileName;
            a.click();
            window.URL.revokeObjectURL(url);

            this.notifications.addUserNotification(
              {
                icon: NotificationMessageIcon.checkmark,
                text: `${this.translationService.getTranslatedContent('downloading', 'Downloading')} ${fileName}`,
                progress: 100,
                status: `${this.translationService.getTranslatedContent('download_complete', 'Download Complete')}`,
              },
              id
            );
            if (this.foldersToZip?.[id]?.socket?.close)
              this.foldersToZip?.[id]?.socket?.close();
            if (this.foldersToZip?.[id]?.subscription)
              this.foldersToZip?.[id]?.subscription?.unsubscribe();
            delete this.foldersToZip[id];
          }
        },
        err => {
          this.handleGenericZipError(id);
        }
      );
  }

  downloadZip(signedUrl: string, id, filename?: string) {
    this.httpClient
      .get(signedUrl, {
        responseType: 'blob',
        reportProgress: true,
        observe: 'events',
      })
      .subscribe(
        res => {
          if (res.type === HttpEventType.DownloadProgress) {
            const percentDone = Math.round((100 * res.loaded) / res.total);
            let message = this.translationService.getTranslatedContent(
              'downloading_zip',
              'Downloading Zip'
            );
            if (filename) message += ` ${filename}`;

            this.notifications.addUserNotification(
              {
                text: message,
                icon: NotificationMessageIcon.fileZip,
                progress: percentDone,
                status: 'Downloading',
              },
              id
            );
          }
          if (res.type === HttpEventType.Response) {
            const lower = filename?.toLowerCase() ?? '';
            const blobType = lower.endsWith('.gz')
              ? 'application/gzip'
              : lower.endsWith('.csv')
                ? 'text/csv'
                : 'application/zip';
            const blob = new Blob([res.body], { type: blobType });
            const a = document.createElement('a');
            document.body.appendChild(a);
            a.style.display = 'none';
            const url = window.URL.createObjectURL(blob);
            a.href = url;
            a.download = filename || 'zip-file.zip';
            a.click();
            window.URL.revokeObjectURL(url);
            let message = this.translationService.getTranslatedContent(
              'downloaded_zip',
              'Downloaded Zip'
            );
            if (filename) message += `: ${filename}`;
            this.notifications.addUserNotification(
              {
                icon: NotificationMessageIcon.checkmark,
                text: message,
                progress: 100,
                status: 'Download Complete',
              },
              id
            );
            this.foldersToZip[id]?.socket?.close();
            if (this.foldersToZip[id]?.subscription)
              this.foldersToZip[id]?.subscription?.unsubscribe();
            delete this.foldersToZip[id];
          }
        },
        err => {
          this.handleGenericZipError(id);
        }
      );
  }

  downloadZipFile(signedUrl: string, folder, index?, prefix?) {
    this.httpClient
      .get(signedUrl, {
        responseType: 'blob',
        reportProgress: true,
        observe: 'events',
      })
      .subscribe(
        res => {
          if (res.type === HttpEventType.DownloadProgress) {
            const percentDone = Math.round((100 * res.loaded) / res.total);
            this.notifications.addUserNotification(
              {
                icon: NotificationMessageIcon.fileZip,
                text: `${this.translationService.getTranslatedContent('downloading', 'Downloading')} <b>${folder?.data?.title || ''}</b>`,
                progress: percentDone,
                status: 'Downloading',
              },
              folder.id
            );
          }
          if (res.type === HttpEventType.Response && res?.body) {
            const blob = new Blob([res.body], {
              type: 'application/zip',
            });
            const a = document.createElement('a');
            document.body.appendChild(a);
            a.style.display = 'none';
            const url = window.URL.createObjectURL(blob);
            a.href = url;
            a.download =
              (folder?.data?.title
                ? index
                  ? `${folder?.data?.title}-${index}`
                  : `${folder?.data?.title}`
                : 'zip-file') + '.zip';
            a.click();
            window.URL.revokeObjectURL(url);
            this.notifications.addUserNotification(
              {
                icon: NotificationMessageIcon.checkmark,
                text: `${this.translationService.getTranslatedContent('downloading', 'Downloading')} <b>${folder?.data?.title || this.translationService.getTranslatedContent('file', 'File')}</b>`,
                progress: 100,
                status: 'Download Complete',
              },
              folder.id
            );
            // }
            this.foldersToZip[folder.id]?.socket?.close();
            if (this.foldersToZip[folder.id]?.subscription)
              this.foldersToZip[folder.id]?.subscription?.unsubscribe();
            delete this.foldersToZip[folder.id];
          }
        },
        err => {
          this.handleGenericError(folder);
        }
      );
  }
}
